<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import clsx from 'clsx'

import type { BetaEditorPreviewDevice } from '@/utils/editor-beta-preview'

const props = withDefaults(defineProps<{
    src?: string | null
    device: BetaEditorPreviewDevice
    unavailableReason?: string | null
}>(), {
    src: null,
    unavailableReason: null,
})

const refreshNonce = ref(0)
const isLoading = ref(true)

const hasPreview = computed(() => Boolean(props.src))
const frameKey = computed(() => `${props.device}-${props.src ?? 'empty'}-${refreshNonce.value}`)

watch(
    () => [props.src, props.device],
    () => {
        isLoading.value = true
    },
    { immediate: true },
)

const onReload = () => {
    if (!hasPreview.value) return

    refreshNonce.value += 1
    isLoading.value = true
}
</script>

<template>
    <section class="beta-site-preview">
        <div :class="clsx(
            'beta-site-preview__frame-shell',
            props.device === 'tablet' && 'is-tablet',
            props.device === 'mobile' && 'is-mobile',
        )">
            <button
                v-if="hasPreview"
                type="button"
                class="beta-site-preview__reload"
                @click="onReload"
            >
                Recarregar
            </button>

            <div v-if="!hasPreview" class="beta-site-preview__empty-state">
                <p class="beta-site-preview__empty-title">Nao foi possivel montar o preview real desta pagina.</p>
                <p class="beta-site-preview__empty-description">
                    {{ props.unavailableReason || 'Verifique a rota publicada e o dominio principal do site.' }}
                </p>
            </div>

            <div v-else class="beta-site-preview__frame-wrapper">
                <div v-if="isLoading" class="beta-site-preview__loading">Carregando...</div>

                <iframe
                    :key="frameKey"
                    :src="props.src ?? undefined"
                    title="Preview real do site-client"
                    class="beta-site-preview__iframe"
                    loading="eager"
                    @load="isLoading = false"
                />
            </div>
        </div>
    </section>
</template>

<style scoped>
.beta-site-preview {
    @apply flex w-full flex-col;
}

.beta-site-preview__frame-shell {
    @apply relative rounded-[34px] border border-[#DCE5F2] bg-white p-3 shadow-[0_24px_60px_rgba(15,23,42,0.12)];
}

.beta-site-preview__frame-shell.is-mobile {
    @apply rounded-[36px] p-4;
}

.beta-site-preview__frame-shell.is-tablet {
    @apply rounded-[36px] p-4;
}

.beta-site-preview__reload {
    @apply absolute right-5 top-5 z-20 inline-flex h-10 items-center justify-center rounded-2xl border border-[#DCE5F2] bg-white px-4 text-sm font-semibold text-[#334760] shadow-[0_10px_28px_rgba(15,23,42,0.08)];
}

.beta-site-preview__frame-wrapper {
    @apply relative overflow-hidden rounded-[24px] border border-[#E3EAF4] bg-[#F8FAFD];
    min-height: calc(100vh - 220px);
}

.beta-site-preview__iframe {
    @apply block h-[calc(100vh-220px)] w-full border-0 bg-white;
}

.beta-site-preview__loading {
    @apply absolute inset-x-0 top-0 z-10 flex h-11 items-center justify-center bg-[rgba(248,250,253,0.92)] text-sm font-medium text-[#5C708C] backdrop-blur-sm;
}

.beta-site-preview__empty-state {
    @apply flex min-h-[420px] flex-col items-center justify-center rounded-[24px] border border-dashed border-[#D6E0EE] bg-[#F8FAFD] px-8 text-center;
}

.beta-site-preview__empty-title {
    @apply text-base font-semibold text-[#11233E];
}

.beta-site-preview__empty-description {
    @apply mt-2 max-w-[34rem] text-sm text-[#60738E];
}
</style>
